import { createLogger, LOG_TOPIC } from '../devMode/devMode';
import { handleEmptyObjects } from '../handleEmptyObjects/handleEmptyObjects';
import { getStorage, removeStorage, setStorage } from '../storage/storage';
import { STORAGE_KEY } from '../storageKeys/storageKeys';

let _logger;
const getLogger = () => {
  if( !_logger ){
    _logger = createLogger( {
      topic: LOG_TOPIC.Session,
      scope: '[SDK] ShoppingAgent >'
    } );
  }
  return _logger;
};


const normalizeTokenCache = ( cache ) => {
  const {
    sessionId = null,
    token = null,
    tokenExpiry = null
  } = cache || {};

  if( !sessionId && !token && !tokenExpiry ){
    return null;
  }

  return {
    sessionId,
    token,
    tokenExpiry
  };
};

export const getShoppingAgentTokenCache = () => {
  return normalizeTokenCache(
    getStorage( {
      key: STORAGE_KEY.shoppingAgentTokenCache
    } )
  );
};

export const setShoppingAgentTokenCache = ( cache ) => {
  const normalizedTokenCache = normalizeTokenCache( cache );

  if( !normalizedTokenCache ){
    return false;
  }

  return setStorage( {
    key: STORAGE_KEY.shoppingAgentTokenCache,
    value: normalizedTokenCache
  } );
};

export const clearShoppingAgentTokenCache = () => {
  return removeStorage( {
    key: STORAGE_KEY.shoppingAgentTokenCache
  } );
};

/**
 * Clears the shopping agent session: token cache + Google chat messages.
 * Single owner of all "clear" behavior — called by the FSM during the Clearing phase.
 */
export const clearShoppingAgentSession = () => {

  const agent = typeof window !== 'undefined' ? window.googleAgent : global.googleAgent;

  if( typeof agent?.clearMessages === 'function' ){
    agent.clearMessages();
    getLogger().log( 'clearMessages called' );
  }
  else {
    getLogger().error( 'clearMessages unavailable', {
      googleAgentExists: !!agent,
      availableMethods: agent ? Object.keys( agent ) : []
    } );
  }
};

export const isShoppingAgentTokenExpired = ( tokenExpiry ) => {
  if( !tokenExpiry ){
    return true;
  }

  const expiryTime = new Date( tokenExpiry ).valueOf();

  if( !expiryTime ){
    return true;
  }

  return expiryTime <= Date.now();
};

/**
 * Formats internal token cache into the shape Google's SDK expects from tokenFunc.
 * @see https://cloud.google.com/dialogflow/cx/docs/concept/integration/shopping-agent
 * @param {object} cache - Normalized token cache { sessionId, token, tokenExpiry }
 * @returns {{ token: string|null, sessionId: string|null, metadata: object|undefined }}
 */
export const formatTokenForGoogle = ( cache ) => {
  const result = {
    token: cache?.token || null,
    sessionId: cache?.sessionId || null
  };

  if( cache?.tokenExpiry ){
    result.metadata = { tokenExpiry: cache.tokenExpiry };
  }

  return result;
};

/**
 * Imperatively pushes token + sessionId to Google's agent instance.
 * Belt-and-suspenders: ensures Google has our credentials even if
 * they don't call tokenFunc when expected.
 *
 * @param {object} cache - Normalized token cache { sessionId, token, tokenExpiry }
 */
export const updateGoogleShoppingAgentSession = ( cache ) => {
  const agent = typeof window !== 'undefined' ? window.googleAgent : global.googleAgent;

  if( typeof agent?.setSessionId !== 'function' || typeof agent?.setToken !== 'function' ){
    getLogger().warn( 'updateGoogleShoppingAgentSession: agent methods unavailable', {
      googleAgentExists: !!agent,
      availableMethods: agent ? Object.keys( agent ) : []
    } );
    return;
  }

  agent.setSessionId( cache?.sessionId );
  agent.setToken( cache?.token );

  getLogger().log( 'updateGoogleShoppingAgentSession', {
    sessionId: cache?.sessionId,
    hasToken: !!cache?.token
  } );
};

/**
 * Module-level singleton: when a network fetch is in-flight, all callers
 * share the same promise instead of firing duplicate requests.
 */
let _pendingTokenFetch = null;

/**
 * Curried token fetch handler — testable independently.
 * Follows composeHelper pattern: lock dependencies, return async executor.
 *
 * @param {object} data - { signal, requestSessionId }
 * @returns {Function} async executor that performs the fetch
 */
export const composeTokenFetch = ( data ) => async() => {
  const { signal = null, requestSessionId = null } = handleEmptyObjects( data );

  try {
    const response = await global.ULTA_SDK?.shoppingAgentModel?.tokenaction?.get( {
      sessionId: requestSessionId
    } );

    if( signal?.aborted ){
      throw new Error( 'Shopping agent token request aborted' );
    }

    const refreshedTokenCache = normalizeTokenCache( response );

    if( !refreshedTokenCache?.sessionId || !refreshedTokenCache?.token ){
      throw new Error( 'No response from token action' );
    }

    setShoppingAgentTokenCache( refreshedTokenCache );

    getLogger().log( 'token fetched', {
      sessionId: refreshedTokenCache.sessionId
    } );

    return refreshedTokenCache;
  }
  catch ( error ){
    getLogger().error( 'token fetch failed', error );
    return null;
  }
  finally {
    _pendingTokenFetch = null;
  }
};

export const getShoppingAgentToken = async( data = {} ) => {
  const {
    sessionId = null,
    forceRefresh = false,
    signal = null
  } = handleEmptyObjects( data );

  const cachedTokenCache = getShoppingAgentTokenCache();

  // ── Derived state ────────────────────────────────────────────
  const hasCachedToken     = !!cachedTokenCache?.token;
  const isCacheExpired     = hasCachedToken && isShoppingAgentTokenExpired( cachedTokenCache.tokenExpiry );
  const shouldUseCache     = !forceRefresh && hasCachedToken && !isCacheExpired;
  const hasSessionMismatch = !!sessionId && !!cachedTokenCache?.sessionId && sessionId !== cachedTokenCache.sessionId;
  const requestSessionId   = forceRefresh ?
    sessionId :
    ( cachedTokenCache?.sessionId || sessionId || null );

  // ── Cache gate ───────────────────────────────────────────────
  if( shouldUseCache ){
    getLogger().log( 'returning cached token (not expired)', {
      cachedSessionId: cachedTokenCache.sessionId,
      callerSessionId: sessionId
    } );
    return cachedTokenCache;
  }

  if( isCacheExpired && hasSessionMismatch ){
    getLogger().warn( 'sessionId mismatch on expired cache, preferring cached', {
      callerSessionId: sessionId,
      cachedSessionId: cachedTokenCache.sessionId
    } );
  }

  // ── Dedup gate ───────────────────────────────────────────────
  if( !forceRefresh && _pendingTokenFetch ){
    getLogger().log( 'joining in-flight token request' );
    return await _pendingTokenFetch;
  }

  // ── Pre-flight abort check ───────────────────────────────────
  if( signal?.aborted ){
    throw new Error( 'Shopping agent token request aborted' );
  }

  // ── Execute fetch ────────────────────────────────────────────
  const fetchToken = composeTokenFetch( { signal, requestSessionId } );
  _pendingTokenFetch = fetchToken();

  return await _pendingTokenFetch;
};

export const requestShoppingAgentToken = getShoppingAgentToken;

/** @internal — test-only: reset the shared promise singleton between tests */
export const resetPendingTokenFetch = () => {
  _pendingTokenFetch = null;
};
